// @flow
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import { gql, useQuery, useMutation } from '@apollo/client';
import { flashMessage as __flashMessage } from '~/app/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getStudioEditorUrl } from '~/lib/studioUrl';
import EditPageMeta from './EditPageMeta';
import PageSettings from './PageSettings';

type CopyPageData = {
  pageId: string,
  createId: string,
  categoryId: string,
  path: string,
  title: string,
  is_root: boolean
}

type Props = {
  isFetching: boolean,
  classes: Object,
  match: {
    params: {
      pageId: string
    }
  },
  page: Object,
  userId: string,
  flashMessage: Function,
  onClickCopy: (CopyPageData) => Promise<*>
};

const CopyPage = (props: Props) => {
  const { classes: c } = props;
  const { id } = useParams();
  const { userId } = useSelector(mapStateToProps);

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));

  // getData
  const { data, loading: isFetching } = useQuery(QUERY, {
    variables: { id }
  });
  const page = data?.page;

  // copyPage
  const [__copyPage, { loading: copyLoading }] = useMutation(ACTION);
  const copyPage = (pageData) => __copyPage({
    variables: { page: pageData }
  });

  const [createFinished, setCreateFinished] = useState(false);
  const [pageId, setPageId] = useState('');
  const [error, setError] = useState();
  const [redirecting, setRedirecting] = useState(false);

  const handleCreateNewPage = async (title, path, slug, category) => {
    setError();
    if (!title || !slug ) {
      flashMessage('Please fill in all fields.', 'ERROR');
      return;
    }

    const data = {
      pageId: page._id,
      creatorId: userId,
      categoryId: category,
      path: `${path}/${slug}`,
      title,
      is_root: path === ''
    };

    try {
      const obj = await copyPage(data);
      const newId = obj?.data?.addPageFromExisting?._id;
      if (!newId) {
        throw new Error('Page copy failed');
      }

      if (page?.editorType === 'studio') {
        setRedirecting(true);
        flashMessage('Studio page copied. Opening Studio editor...');
        window.location.assign(getStudioEditorUrl(newId));
        return;
      }

      setPageId(newId);
      setCreateFinished(true);
      flashMessage('Successfully copied the page!');
    } catch (e) {
      setError(e.message);
    }
  }

  if (isFetching) return <span>Loading...</span>;
  if (!page) return <span>Page not found.</span>;

  return (
    <div className={c.container}>
      <div className={c.header}>
        <div className={c.title}>
          Copy Page From: <span>{page.title}</span>
        </div>
      </div>
      <div className={c.body}>
        <PageSettings
          disabled={createFinished || redirecting}
          copyLoading={copyLoading || redirecting}
          loadingLabel={redirecting ? 'Opening editor…' : 'Loading...'}
          title={`${page.title} copy`}
          templateId={page?.template?.templateId}
          templateName={page?.editorType === 'studio' ? 'Studio' : undefined}
          editorType={page?.editorType === 'studio' ? 'studio' : 'genesis'}
          onCreate={handleCreateNewPage}
          copyError={error}
        />
      </div>
      { createFinished &&
        <EditPageMeta pageId={pageId} />
      }
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '20px 30px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  }
};

/* =============================================================================
=    GraphQL: Get categories/page Title
============================================================================= */
const QUERY = gql`
  query getData ($id: ID!) {
    page (id: $id) {
      _id
      editorType
      title
      template {
        templateId
      }
    }
  }
`;

/* =============================================================================
=    GraphQL: Copy from existing page
============================================================================= */
const ACTION = gql`
  mutation copyPage($page: CopyPageInput) {
    addPageFromExisting(page: $page) {
      _id
    }
  }
`;

/* =============================================================================
=    Redux
============================================================================= */
const mapStateToProps = state => ({
  userId: state.app.user && state.app.user._id
});

export default injectSheet(styles)(CopyPage);
